import { toAmount } from '@/features/pricing';
import type { ItemHistoryRow } from '@/features/orders/contracts';

/** What a customer usually orders of one SKU, read from its own order history. */
export interface UsualQuantity {
  /** The average quantity per order, snapped to the SKU's minimum and multiple. */
  readonly quantity: number;
  readonly orderCount: number;
  readonly lastOrderedAt: string;
}

/**
 * The history endpoint answers totals per SKU over a period, not the last line of the last order,
 * so the "usual" quantity is the average per order. It is snapped to the SKU's rules before it is
 * offered: suggesting 7 of something sold in multiples of 5 would hand the buyer a line the cart
 * then rejects.
 */
export function usualQuantity(
  row: Pick<ItemHistoryRow, 'total_quantity' | 'order_count' | 'last_ordered_at'>,
  minimumOrderQuantity: string | number | null | undefined,
  orderMultiple: string | number | null | undefined,
): UsualQuantity | null {
  const total = toAmount(row.total_quantity);
  if (total === null || total <= 0 || row.order_count <= 0) {
    return null;
  }

  const average = total / row.order_count;
  const multiple = toAmount(orderMultiple);
  const minimum = toAmount(minimumOrderQuantity) ?? 0;
  const snapped =
    multiple !== null && multiple > 0 ? Math.round(average / multiple) * multiple : average;
  const quantity = Math.round(Math.max(snapped, minimum, multiple ?? 0) * 1_000) / 1_000;

  return { quantity, orderCount: row.order_count, lastOrderedAt: row.last_ordered_at };
}

/** 90 days back from a São Paulo calendar day — the window the order history screen opens on. */
export function historyWindowStart(today: string): string {
  const date = new Date(`${today}T00:00:00-03:00`);
  date.setUTCDate(date.getUTCDate() - 90);
  return date.toISOString().slice(0, 10);
}
